import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, ChevronLeft, ChevronRight, Coins, Hash, LayoutGrid, MessageSquare, MonitorSmartphone, RefreshCw, Search, X } from "lucide-react";
import { Chip, Panel, PanelTitle, ShareBar, StatGrid } from "@/components/panels";
import { dateLabel, number } from "@/components/sessions/sessionData";
import { creditUnit, formatCount } from "./alloyView";
import ContributionSkyline, { buildGrid, computeStats, dayMs } from "./ContributionSkyline";
import { initialsOf, percentLabel } from "./usageByPerson";
import "./usageByPerson.css";

// One team member's usage of the agent, in ARC's brand-card box with the dashboard's colours: a rail with the
// identity, a section search and the grouped sections, and a pane that shows one section at a time.
// ⌘K focuses the search, ⌥1–4 switch sections, Esc closes (or clears the search first). Radix owns the focus
// trap and returns focus to the row that opened the card.

const PALETTE = { light: ["#C7DDF7", "#7FB2E8", "#2F6DB5", "#003B8D"], dark: ["#12325C", "#1E4F8F", "#4690D9", "#9CC6F0"] };

export const SECTIONS = [
  { id: "overview", label: "Overview", title: "Overview", intro: "How this person uses the agent, at a glance.", color: "#003B8D", icon: LayoutGrid },
  { id: "activity", label: "Activity", title: "Activity", intro: "A year of conversations: one box for each day this person started one.", color: "#4690D9", icon: BarChart3 },
  { id: "credits", label: "Credits", title: "Credits", intro: "What this person's conversations cost in recorded Alloy credits.", color: "#16A34A", icon: Coins },
  { id: "conversations", label: "Conversations", title: "Conversations", intro: "Every loaded conversation, newest first, with its recorded cost.", color: "#0F2442", icon: MessageSquare },
];
const NAV_GROUPS = [["Person", ["overview", "activity"]], ["Money", ["credits"]], ["Work", ["conversations"]]];

// Amounts print through `unit` (creditUnit): Alloy tokens for a brand on tokens, dollars otherwise. The Credits
// section keeps its id and becomes "Tokens" on a tokens brand; the model-token panels say "Model tokens" either way.
const DOLLARS = creditUnit();
function sectionsFor(unit) {
  if (!unit.tokens) return SECTIONS;
  return SECTIONS.map((entry) => (entry.id === "credits"
    ? { ...entry, label: "Tokens", title: "Tokens", intro: "What this person's conversations cost in recorded Alloy tokens." }
    : entry));
}

// A day key (YYYY-MM-DD) is a calendar day, not an instant: show it in UTC, or it reads as the day before west of UTC.
const dayZone = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? "UTC" : undefined);
const shortDate = (value) => (value && !Number.isNaN(new Date(value).getTime())
  ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: dayZone(value) })
  : "—");
const longDate = (value) => (value && !Number.isNaN(new Date(value).getTime())
  ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: dayZone(value) })
  : "—");
const tokens = (value) => (value == null ? "—" : formatCount(value));

function roleLabel(role) {
  const text = String(role || "member");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function ConversationItem({ row, unit = DOLLARS }) {
  const recorded = row.knownCostChunks > 0 && row.knownActualCostUsd != null;
  const slack = row.source === "Slack";
  const Icon = slack ? Hash : MonitorSmartphone;
  return (
    <li>
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#EDF5FF] text-sm-primary" aria-hidden="true"><Icon size={16} /></span>
      <div style={{ minWidth: 0 }}>
        <b>{row.title || (slack ? "Slack conversation" : "Dashboard conversation")}</b>
        <small>{slack ? "Slack" : "Dashboard"} · {dateLabel(row.updatedAt || row.startedAt)} · {number(row.totalTokens)} tokens · {number(row.requestCount)} requests</small>
      </div>
      <div className="right">
        <strong className={`tnum ${recorded ? "" : "none"}`}>{recorded ? unit.recorded(row.knownActualCostUsd) : "—"}</strong>
        <small>{recorded ? "Recorded" : "Not recorded"}</small>
      </div>
    </li>
  );
}

const DAY = 86400000;
const monthStartOf = (ms, step) => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + step, 1);
};

/** The heat map for the last year or for one calendar month, with the totals for the range shown. */
function Skyline({ person, endDate, span, step, onSpan, onStep }) {
  const nf = new Intl.NumberFormat();
  const data = useMemo(() => person.conversationsPerDay || [], [person.conversationsPerDay]);
  const end = dayMs(endDate);
  const monthStart = monthStartOf(end, step);
  const start = span === "month" ? monthStart : undefined;
  const last = span === "month" ? Math.min(monthStartOf(end, step + 1) - DAY, end) : end;
  const stats = useMemo(() => computeStats(buildGrid(data, last, 0, start).cells), [data, last, start]);
  const earliest = data.reduce((min, day) => Math.min(min, dayMs(day.date)), end);
  const monthName = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" }).format(monthStart);
  const noun = stats.total === 1 ? "conversation" : "conversations";
  const controls = (
    <div className="pc-range">
      <div role="group" aria-label="Time range" className="pc-range-seg">
        <button type="button" aria-pressed={span === "year"} onClick={() => onSpan("year")}>Year</button>
        <button type="button" aria-pressed={span === "month"} onClick={() => onSpan("month")}>Month</button>
      </div>
      {span === "month" ? (
        <div role="group" aria-label="Choose a month" className="pc-range-month">
          <button type="button" aria-label="Previous month" disabled={monthStart <= monthStartOf(earliest, 0)} onClick={() => onStep(step - 1)}><ChevronLeft size={16} aria-hidden="true" /></button>
          <span aria-live="polite">{monthName}</span>
          <button type="button" aria-label="Next month" disabled={step >= 0} onClick={() => onStep(step + 1)}><ChevronRight size={16} aria-hidden="true" /></button>
        </div>
      ) : null}
    </div>
  );
  return (
    <div className="pc-sky">
      <ContributionSkyline
        key={person.key}
        data={data}
        endDate={last}
        startDate={start}
        unit="conversation"
        palette={PALETTE}
        showStats={false}
        actions={controls}
        title={<><span className="font-semibold tabular-nums">{nf.format(stats.total)}</span> {noun} started in {span === "month" ? monthName : "the last year"}</>}
        footer={null}
      />
      <dl className="pc-year">
        <div><dt>{span === "month" ? `${monthName} total` : "1 year total"}</dt><dd>{nf.format(stats.total)} {noun}</dd></div>
        <div><dt>Busiest day</dt><dd>{nf.format(stats.busiest.count)} · {stats.busiest.date ? shortDate(stats.busiest.date) : "—"}</dd></div>
        <div><dt>Longest streak</dt><dd>{nf.format(stats.longest.days)} {stats.longest.days === 1 ? "day" : "days"}</dd></div>
      </dl>
    </div>
  );
}

export default function PersonCard({ person, open, onOpenChange, workspaceKnownCostUsd = 0, slackMatched = false, recentWindow = null, coverage = null, loading = false, onRefresh, returnFocusTo = null, unit = DOLLARS }) {
  const sections = sectionsFor(unit);
  const [section, setSection] = useState("overview");
  const [query, setQuery] = useState("");
  // The heat map's range, shared by Overview and Activity: the last year, or one month (step 0 = this month).
  const [span, setSpan] = useState("year");
  const [step, setStep] = useState(0);
  const pickSpan = (next) => {
    setSpan(next);
    setStep(0);
  };
  const searchRef = useRef(null);
  const closeRef = useRef(null);
  const paneRef = useRef(null);

  useEffect(() => {
    setSection("overview");
    setQuery("");
    setSpan("year");
    setStep(0);
  }, [person?.key, open]);

  const endDate = useMemo(() => (coverage?.asOf ? coverage.asOf.slice(0, 10) : new Date().toISOString().slice(0, 10)), [coverage?.asOf]);
  const stats = useMemo(() => (person ? computeStats(buildGrid(person.conversationsPerDay || [], dayMs(endDate)).cells) : null), [person, endDate]);

  if (!person) return null;
  const member = person.person;
  const name = person.name;
  const slackOnly = person.kind === "slack";
  const channels = person.channels || { Dashboard: 0, Slack: 0 };
  const recorded = person.recordedSessions > 0;
  const counts = {
    activity: stats?.total ? [number(stats.total), ""] : null,
    credits: recorded ? [unit.recorded(person.totals.actualCostUsd), "ok"] : null,
    conversations: person.sessions.length ? [number(person.sessions.length), ""] : null,
  };
  const normalized = query.trim().toLowerCase();
  const matches = (entry) => !normalized || entry.label.toLowerCase().includes(normalized) || entry.intro.toLowerCase().includes(normalized);
  const visibleGroups = NAV_GROUPS.map(([label, ids]) => [label, ids.map((id) => sections.find((entry) => entry.id === id)).filter(matches)]).filter(([, entries]) => entries.length);
  const current = sections.find((entry) => entry.id === section) || sections[0];

  const go = (id) => {
    setSection(id);
    if (paneRef.current?.scrollTo) paneRef.current.scrollTo(0, 0);
  };
  const onKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
      return;
    }
    const digit = event.altKey ? /^Digit([1-9])$/.exec(event.code) : null;
    if (digit && Number(digit[1]) <= sections.length) {
      event.preventDefault();
      go(sections[Number(digit[1]) - 1].id);
    }
  };
  const onSearchKeyDown = (event) => {
    if (event.key === "Escape" && query) {
      event.preventDefault();
      event.stopPropagation();
      setQuery("");
    }
  };

  const windowLabel = recentWindow ? `${shortDate(recentWindow.start)} – ${longDate(recentWindow.end)}` : "the newest 31 days";
  const shareParts = person.share == null ? [] : [
    { key: "person", percent: Math.min(100, person.share), barClass: "bg-sm-primary", legend: `${name} ${unit.recorded(person.knownCostUsd)}` },
    { key: "rest", percent: Math.max(0, 100 - person.share), barClass: "bg-[#C7DDF7]", legend: "Everyone else" },
  ];
  const firstSeen = person.sessions.reduce((earliest, row) => {
    const at = row.startedAt || row.updatedAt;
    return at && (!earliest || at < earliest) ? at : earliest;
  }, null);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="pc-backdrop" />
        <DialogPrimitive.Content
          className="pc"
          data-testid="person-card"
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          onOpenAutoFocus={(event) => { event.preventDefault(); closeRef.current?.focus(); }}
          onCloseAutoFocus={(event) => {
            // Back to the row that opened the card, even in browsers that do not focus a button on click.
            const node = returnFocusTo?.current;
            if (node && typeof node.focus === "function" && node.isConnected) {
              event.preventDefault();
              node.focus();
            }
          }}
        >
          <div className="pc-shell">
            <aside className="pc-rail">
              <div className="pc-ident">
                <div className={`pc-av ${member?.status === "revoked" ? "former" : ""}`} aria-hidden="true">{initialsOf(name)}</div>
                <div style={{ minWidth: 0 }}>
                  <DialogPrimitive.Title asChild><h2>{name}</h2></DialogPrimitive.Title>
                  <div className="pc-who">{member ? `${roleLabel(member.role)} · ${member.email}` : slackOnly ? "In Slack · not on the dashboard team" : "A dashboard sign-in that is no longer on the team"}</div>
                  <div className="pc-chips">
                    {member?.status === "revoked" ? <Chip tone="warn">Former member</Chip> : member ? <Chip tone="ok">Active</Chip> : slackOnly ? null : <Chip tone="quiet">Earlier sign-in</Chip>}
                    {channels.Slack > 0 ? <Chip tone="busy" testId="person-card-slack-chip">Slack</Chip> : null}
                    {member?.you ? <Chip tone="navy" dot={false}>You</Chip> : null}
                  </div>
                </div>
              </div>
              <div className="pc-search">
                <Search size={16} aria-hidden="true" />
                <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={onSearchKeyDown} placeholder="Search sections" autoComplete="off" aria-label="Search sections" />
                <kbd aria-hidden="true">⌘K</kbd>
              </div>
              <nav className="pc-navs" aria-label="Sections">
                {visibleGroups.length === 0 ? <div className="pc-nomatch">Nothing matches “{query}”.</div> : visibleGroups.map(([label, entries]) => (
                  <div key={label}>
                    <div className="pc-grp">{label}</div>
                    {entries.map((entry) => {
                      const Icon = entry.icon;
                      const count = counts[entry.id];
                      const index = sections.findIndex((item) => item.id === entry.id) + 1;
                      return (
                        <button type="button" key={entry.id} className="pc-nav" aria-current={section === entry.id ? "true" : "false"} onClick={() => go(entry.id)} data-testid={`person-card-nav-${entry.id}`}>
                          <span className="pc-ti" style={{ background: entry.color }}><Icon size={15} strokeWidth={2.1} aria-hidden="true" /></span>
                          <span className="pc-label">{entry.label}</span>
                          {count ? <span className={`pc-cnt ${count[1]}`}>{count[0]}</span> : null}
                          <span className="pc-kb" aria-hidden="true">⌥{index}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </nav>
              <div className="pc-rail-foot">
                <Link className="pc-btn-primary" to="/agent/sessions">Open on Sessions <ArrowRight size={14} aria-hidden="true" /></Link>
                <button type="button" className="pc-btn-ghost" onClick={onRefresh} disabled={loading || !onRefresh}><RefreshCw size={13} aria-hidden="true" className={loading ? "animate-spin" : ""} /> Refresh</button>
              </div>
            </aside>

            <div className="pc-pane" ref={paneRef}>
              <div className="pc-pane-top">
                <div style={{ minWidth: 0 }}>
                  <h3>{current.title}</h3>
                  <div className="pc-intro">{current.intro}</div>
                </div>
                <div className="pc-pane-actions">
                  <DialogPrimitive.Close asChild>
                    <button type="button" className="pc-close" aria-label="Close" ref={closeRef}><X size={16} aria-hidden="true" /></button>
                  </DialogPrimitive.Close>
                </div>
              </div>

              {section === "overview" ? (
                <div className="pc-bento" data-testid="person-card-overview">
                  <Panel aria-labelledby="pc-ov-recent">
                    <PanelTitle id="pc-ov-recent" aside={<span className="text-xs text-sm-ink/65">Recorded {unit.name} · {windowLabel}</span>}>Last 31 days</PanelTitle>
                    <StatGrid className="mt-3" stats={[
                      { label: unit.tokens ? "Tokens used" : "Credits used", value: unit.recorded(person.recent.actualCostUsd), sub: person.recent.requestCount == null ? "No recorded requests" : `${number(person.recent.requestCount)} recorded requests` },
                      { label: "Conversations", value: number(person.recent.sessions), sub: `Last active ${shortDate(person.lastActiveAt)}` },
                      { label: `Share of loaded ${unit.noun}`, value: percentLabel(person.share), sub: `of ${unit.recorded(workspaceKnownCostUsd)} recorded` },
                    ]} />
                    {shareParts.length ? <ShareBar parts={shareParts} note="All loaded history" label={`${name} ${percentLabel(person.share)} of recorded ${unit.noun}`} /> : null}
                  </Panel>
                  <Panel aria-labelledby="pc-ov-tokens">
                    <PanelTitle id="pc-ov-tokens" aside={<span className="text-xs text-sm-ink/65">All loaded history · input and output, all models</span>}>Model tokens</PanelTitle>
                    <StatGrid className="mt-3" stats={[
                      { label: "Input", value: tokens(person.totals.inputTokens) },
                      { label: "Output", value: tokens(person.totals.outputTokens) },
                      { label: "Cached input", value: tokens(person.totals.cachedTokens) },
                    ]} />
                  </Panel>
                  <Panel aria-labelledby="pc-ov-year">
                    <PanelTitle id="pc-ov-year" aside={<span className="text-xs text-sm-ink/65">Conversations started per day · hover a day</span>}>{span === "month" ? "A month of conversations" : "A year of conversations"}</PanelTitle>
                    {stats?.total ? <Skyline person={person} endDate={endDate} span={span} step={step} onSpan={pickSpan} onStep={setStep} /> : <p className="pc-empty mt-3">No conversation dates loaded yet.</p>}
                  </Panel>
                  <Panel aria-labelledby="pc-ov-convs">
                    <PanelTitle id="pc-ov-convs" aside={<button type="button" className="pc-text-link" onClick={() => go("conversations")}>See all <ArrowRight size={12} aria-hidden="true" /></button>}>Recent conversations</PanelTitle>
                    {person.sessions.length ? <ul className="pc-list mt-2">{person.sessions.slice(0, 3).map((row) => <ConversationItem key={row.id} row={row} unit={unit} />)}</ul> : <p className="pc-empty mt-3">No conversations loaded.</p>}
                  </Panel>
                </div>
              ) : null}

              {section === "activity" ? (
                <div className="pc-bento" data-testid="person-card-activity">
                  <Panel aria-labelledby="pc-act-year">
                    <PanelTitle id="pc-act-year" aside={<span className="text-xs text-sm-ink/65">One box per day, taller is busier. In 3D, drag to orbit.</span>}>Conversations per day</PanelTitle>
                    {stats?.total ? <Skyline person={person} endDate={endDate} span={span} step={step} onSpan={pickSpan} onStep={setStep} /> : <p className="pc-empty mt-3">No conversation dates loaded yet.</p>}
                  </Panel>
                  <Panel aria-labelledby="pc-act-sources">
                    <PanelTitle id="pc-act-sources">Where the conversations started</PanelTitle>
                    <StatGrid className="mt-3" stats={[
                      { label: "Dashboard chat", value: number(channels.Dashboard), sub: channels.Dashboard === 1 ? "conversation" : "conversations" },
                      { label: "Slack", value: number(channels.Slack), sub: channels.Slack === 1 ? "conversation" : "conversations" },
                      { label: "First seen", value: shortDate(firstSeen), sub: `Last active ${shortDate(person.lastActiveAt)}` },
                    ]} />
                    <p className="pc-note">
                      {slackMatched
                        ? "Dashboard chats are private, so only your own show here. Slack conversations are matched by Slack email, and a thread counts for whoever started it. Scheduled jobs and direct API calls are not assigned to people."
                        : "Dashboard chats are private, so only your own show here. Slack, scheduled jobs and direct API calls are not assigned to people."}
                    </p>
                  </Panel>
                </div>
              ) : null}

              {section === "credits" ? (
                <div className="pc-bento" data-testid="person-card-credits">
                  <Panel aria-labelledby="pc-cr-used">
                    <PanelTitle id="pc-cr-used" aside={<span className="text-xs text-sm-ink/65">Recorded Alloy charges</span>}>{unit.tokens ? "Tokens used" : "Credits used"}</PanelTitle>
                    <StatGrid className="mt-3" stats={[
                      { label: "Last 31 days", value: unit.recorded(person.recent.actualCostUsd), sub: windowLabel },
                      { label: "All loaded history", value: unit.recorded(person.totals.actualCostUsd), sub: coverage?.since ? `Since ${longDate(coverage.since)}` : "Current connection" },
                      { label: "Recorded", value: `${number(person.recordedSessions)} / ${number(person.sessions.length)}`, sub: "conversations with a cost" },
                    ]} />
                    <p className="pc-note">Recorded costs cover the current connection{coverage?.since ? ` since ${longDate(coverage.since)}` : ""}{coverage?.asOf ? `, through ${dateLabel(coverage.asOf)}` : ""}. A dash means the cost is unavailable, never zero.</p>
                  </Panel>
                  <Panel aria-labelledby="pc-cr-tokens">
                    <PanelTitle id="pc-cr-tokens" aside={<span className="text-xs text-sm-ink/65">All loaded history</span>}>Model tokens</PanelTitle>
                    <StatGrid className="mt-3" stats={[
                      { label: "Input tokens", value: tokens(person.totals.inputTokens) },
                      { label: "Output tokens", value: tokens(person.totals.outputTokens) },
                      { label: "Cached input", value: tokens(person.totals.cachedTokens) },
                    ]} />
                  </Panel>
                </div>
              ) : null}

              {section === "conversations" ? (
                <div className="pc-bento" data-testid="person-card-conversations">
                  <Panel aria-labelledby="pc-cv-list">
                    <PanelTitle id="pc-cv-list" aside={<span className="text-xs text-sm-ink/65">{number(person.sessions.length)} loaded · {unit.recorded(person.totals.actualCostUsd)}{unit.tokens ? " tokens" : ""}</span>}>Conversations</PanelTitle>
                    {person.sessions.length ? <ul className="pc-list mt-2">{person.sessions.map((row) => <ConversationItem key={row.id} row={row} unit={unit} />)}</ul> : <p className="pc-empty mt-3">No conversations loaded.</p>}
                    <div className="pp-foot"><span>Transcripts and cost comparisons are on Sessions.</span><Link to="/agent/sessions">Open on Sessions <ArrowRight size={12} aria-hidden="true" /></Link></div>
                  </Panel>
                </div>
              ) : null}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
